import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { Container, FadeIn, SectionHeading, SectionLabel } from "@/components/Section";
import SEOHead from "@/components/SEOHead";
import NextStepsBlock from "@/components/NextStepsBlock";
import SocialEcosystem from "@/components/SocialEcosystem";
import StayConnected from "@/components/StayConnected";
import { COMMUNITY, IMG } from "@/lib/data";

const THEMES = [
  "Island cultures",
  "African diaspora",
  "Caribbean heritage",
  "Migration stories",
  "Ancestral memory",
  "Afro-futurism",
  "Future identities",
  "Countries impacted by colonialism",
];

export default function Community() {
  return (
    <div data-testid="community-page">
      <SEOHead
        cmsPage="community"
        title="Community & Culture · Windrush, Heritage & Global Majority Storytelling · Temple of Kulture"
        description="Windrush, Kulture Kraft Market, heritage storytelling and global majority celebration. African diaspora, Caribbean, island and Afro-futurist programming in Medway, London & Kent."
        image={IMG.windrushEvergreen}
      />
      <PageHero
        cmsPage="community"
        eyebrow="Community & Culture"
        title={
          <>
            Roots, diaspora, <br />
            <span className="text-amber-400">futures.</span>
          </>
        }
        subtitle="Programmes, markets and immersive cultural storytelling celebrating global majority heritage and the worlds we're building next."
        image={IMG.dance2}
        testId="community-hero"
      />

      <section className="py-24 sm:py-32">
        <Container>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {COMMUNITY.map((c, i) => (
              <FadeIn key={c.title} delay={i * 0.05}>
                <Link
                  to={c.href || "/whats-on"}
                  data-testid={`community-card-${i}`}
                  className="group block relative overflow-hidden border border-white/5 hover:border-amber-400/40 transition-colors aspect-[4/5]"
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-[1500ms] group-hover:scale-110"
                    style={{ backgroundImage: `url(${c.image})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <h3 className="font-display font-bold text-xl sm:text-2xl leading-tight">
                      {c.title}
                    </h3>
                    <p className="mt-3 text-sm text-zinc-300 leading-relaxed">
                      {c.desc}
                    </p>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-24 border-y border-amber-500/10 bg-black">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12">
            <FadeIn className="lg:col-span-5">
              <SectionLabel>Cultural themes</SectionLabel>
              <SectionHeading className="mt-6">
                Celebration as <br />
                <span className="text-amber-400">storytelling.</span>
              </SectionHeading>
              <p className="mt-6 text-zinc-300 leading-relaxed max-w-md">
                We explore complex histories through joy, creativity and
                world-building — not heavy political messaging. Pride, beauty
                and imagination as the starting point.
              </p>
            </FadeIn>
            <FadeIn delay={0.15} className="lg:col-span-7">
              <ul className="flex flex-wrap gap-3">
                {THEMES.map((t) => (
                  <li
                    key={t}
                    className="border border-amber-400/30 px-5 py-3 text-sm uppercase tracking-[0.2em] font-bold text-amber-400 hover:bg-amber-400/10 transition-colors"
                  >
                    {t}
                  </li>
                ))}
              </ul>
            </FadeIn>
          </div>
        </Container>
      </section>

      <SocialEcosystem
        eyebrow="Heritage in motion"
        title="Community always on"
        copy="Clips from Kulture Kraft Market, Windrush rehearsals, family days and global majority cultural moments — across every platform."
      />

      <NextStepsBlock
        title="Continue the journey"
        eyebrow="Where to next"
        steps={[
          {
            label: "Step inside",
            title: "Windrush Immersive Experience",
            desc: "A flagship journey through memory, movement and legacy.",
            cta: "Pre-book",
            to: "/shows/windrush-immersive-experience",
            testid: "community-next-windrush",
          },
          {
            label: "7 – 16",
            title: "After School Clubs",
            desc: "Weekly clubs in Medway & Kent: projection mapping, AI storytelling, music tech.",
            cta: "Discover",
            to: "/after-school-clubs",
            testid: "community-next-after-school",
          },
          {
            label: "For families",
            title: "Home Education programme",
            desc: "Wed & Thu sessions for global majority families across Medway.",
            cta: "Learn more",
            to: "/home-education",
            testid: "community-next-home-ed",
          },
          {
            label: "Under 12",
            title: "Interactive Playground",
            desc: "Projection floors, AR sandboxes and sensory-aware play.",
            cta: "Sessions",
            to: "/interactive-playground",
            testid: "community-next-playground",
          },
        ]}
      />

      {/* STAY CONNECTED — community-flavoured dual conversion block */}
      <div className="bg-black border-t border-amber-500/10">
        <StayConnected
          pageSource="community"
          testid="community-stay-connected"
          whatsappHeadline="STAY CLOSE TO THE FIRE"
          whatsappTagline="Heritage drops, Windrush dates, diaspora gatherings — straight to your WhatsApp."
          bullets={[
            "Windrush programming & ticket releases",
            "Kulture Kraft Market dates",
            "Heritage storytelling events",
            "Diaspora-led festivals and socials",
            "Community calls & casting opportunities",
          ]}
        />
      </div>
    </div>
  );
}
